import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import type { ProcessStep } from '../types';

interface Props {
  steps: ProcessStep[];
}

function StepCard({ number, icon, title, desc, index }: ProcessStep & { index: number }) {
  const { ref, isVisible } = useScrollAnimation(0.1);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`relative flex flex-col items-center text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 130}ms` }}
    >
      {/* Connector line */}
      {index < 3 && (
        <div className="hidden lg:block absolute top-8 left-1/2 w-full h-px"
          style={{ background: 'linear-gradient(90deg, rgba(200,169,106,0.4), rgba(200,169,106,0.1))', zIndex: 0 }}
        />
      )}

      <div className="relative z-10 flex flex-col items-center">
        {/* Number badge */}
        <div
          className="w-16 h-16 flex items-center justify-center rounded-full mb-5 relative"
          style={{ background: 'linear-gradient(135deg, #C8A96A, #A8894F)' }}
        >
          <span className="font-playfair text-black font-bold text-xl">{number}</span>
          <div className="absolute -inset-1 rounded-full border border-[#C8A96A]/30" />
        </div>

        <div className="w-10 h-10 flex items-center justify-center bg-[#0D1E35] border border-[#C8A96A]/25 rounded-xl mb-4">
          <i className={`${icon} text-[#C8A96A] text-base`} />
        </div>

        <h3 className="font-playfair text-white font-bold text-base mb-2">{title}</h3>
        <p className="text-white/50 text-sm leading-relaxed max-w-48">{desc}</p>
      </div>
    </div>
  );
}

export default function ServiceProcess({ steps }: Props) {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();

  return (
    <section className="py-20 bg-[#071120] relative overflow-hidden">
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-64 rounded-full opacity-6 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, #C8A96A, transparent)' }}
      />

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10">
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-14 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium mb-3">How It Works</p>
          <h2 className="font-playfair text-3xl md:text-5xl font-bold text-white leading-tight">
            Simple{' '}
            <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">
              Process
            </span>
            , Brilliant Results
          </h2>
          <p className="text-white/50 mt-3 max-w-lg mx-auto text-sm md:text-base">
            From your first call to final sign-off, we make every electrical installation in Sleaford straightforward.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
          {steps.map((step, i) => (
            <StepCard key={step.title} {...step} index={i} />
          ))}
        </div>

        <div className="mt-14 text-center">
          <a
            href="tel:01529265060"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-black font-bold px-10 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
          >
            <i className="ri-phone-fill text-lg" />
            Start with a Free Call — 01529 265060
          </a>
        </div>
      </div>
    </section>
  );
}
